import { useEffect } from 'react';
import { Paper, Text, Group, Badge, Stack, ThemeIcon } from '@mantine/core';
import { IconTruck, IconNavigation } from '@tabler/icons-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLiveTracking } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

// Custom truck icon using a div icon (avoids Vite stripping default Leaflet marker icons)
const truckIcon = L.divIcon({
  className: '',
  html: `<div style="
    background: #228be6;
    border: 2px solid #fff;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  ">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
      fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0"/>
      <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0"/>
      <path d="M5 17h-2v-4m-1-8h11v12m-4 0h6m4 0h2v-6h-8m0-5h5l3 5"/>
      <path d="M3 9l4 0"/>
    </svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

function headingToDirection(heading: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(heading / 45) % 8];
}

/** Keeps the map centered on the driver's position as it updates. */
function MapUpdater({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom(), { animate: true });
  }, [map, lat, lng]);
  return null;
}

interface LiveTrackingMapProps {
  orderId: number;
}

export default function LiveTrackingMap({ orderId }: LiveTrackingMapProps) {
  const { data: live } = useLiveTracking(orderId);

  if (!live) return null;

  // Delivered state
  if (live.status === 'delivered') {
    return (
      <Paper p="md" withBorder radius="md">
        <Group gap="sm">
          <ThemeIcon variant="light" color="green" size="lg">
            <IconTruck size={20} />
          </ThemeIcon>
          <Text fw={600} c="green">Order has been delivered</Text>
        </Group>
      </Paper>
    );
  }

  // No GPS data yet
  if (!live.location) {
    return (
      <Paper p="xl" withBorder radius="md" style={{ textAlign: 'center' }}>
        <Stack align="center" gap="xs">
          <ThemeIcon variant="light" color="gray" size="xl">
            <IconTruck size={24} />
          </ThemeIcon>
          <Text fw={500} c="dimmed">Live tracking not available yet</Text>
          <Text size="xs" c="dimmed">
            GPS data will appear once the driver begins the route.
          </Text>
        </Stack>
      </Paper>
    );
  }

  const { latitude, longitude, speed_mph, heading } = live.location;

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden', position: 'relative' }}>
      <MapContainer
        center={[latitude, longitude]}
        zoom={14}
        scrollWheelZoom={false}
        style={{ height: 350, width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater lat={latitude} lng={longitude} />
        <Marker position={[latitude, longitude]} icon={truckIcon}>
          <Popup>
            <div style={{ minWidth: 160 }}>
              <strong>Delivery Vehicle</strong>
              {live.vehicle && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {live.vehicle.make} {live.vehicle.model}
                  <br />
                  Plate: {live.vehicle.license_plate}
                </div>
              )}
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {speed_mph.toFixed(0)} mph &middot; Heading {headingToDirection(heading)}
              </div>
            </div>
          </Popup>
        </Marker>
      </MapContainer>

      {/* Info overlay */}
      <div style={{
        position: 'absolute',
        bottom: 12,
        left: 12,
        zIndex: 1000,
        pointerEvents: 'none',
      }}>
        <Paper p="xs" radius="sm" shadow="md" style={{ pointerEvents: 'auto' }}>
          <Group gap="xs">
            {live.vehicle && (
              <Badge variant="light" color="blue" size="sm" leftSection={
                <IconTruck size={12} style={{ marginTop: 2 }} />
              }>
                {live.vehicle.make} {live.vehicle.model} &middot; {live.vehicle.license_plate}
              </Badge>
            )}
            <Badge variant="light" color="gray" size="sm" leftSection={
              <IconNavigation size={12} style={{ marginTop: 2 }} />
            }>
              {speed_mph.toFixed(0)} mph {headingToDirection(heading)}
            </Badge>
            {live.eta && (
              <Badge variant="light" color="green" size="sm">
                ETA {dayjs(live.eta).format('h:mm A')}
              </Badge>
            )}
          </Group>
        </Paper>
      </div>
    </Paper>
  );
}
